import { useState } from "react";

import * as Sentry from "@sentry/browser";
import {
  cn,
  EditIcon,
  Alert,
  Button,
  ButtonGroup,
  CopyButton,
  TextField,
} from "@stacklet/ui";
import { Controller, useForm } from "react-hook-form";
import { graphql, useFragment, useMutation } from "react-relay";

import { useAlertContext } from "app/contexts";
import { SetAwsAccessRolesMutation } from "app/mutations/SetAwsAccessRoles";

import { schema, type FormData } from "./schema";

import type { GatewayRolesForm_awsAccessRoles$key } from "./__generated__/GatewayRolesForm_awsAccessRoles.graphql";
import type {
  SetAwsAccessRolesMutation$data,
  SetAwsAccessRolesMutation as SetAwsAccessRolesMutationType,
} from "app/mutations/__generated__/SetAwsAccessRolesMutation.graphql";
import type { RecordSourceSelectorProxy } from "relay-runtime";
import type { ZodSchema } from "zod";

interface ValidRoleConnectionRowProps {
  isValidConnection?: string | null; // undefined signifies not yet validated
  isValidRoleArn?: boolean; // undefined signifies not yet validated
}

function ValidRoleConnectionRow({
  isValidRoleArn,
  isValidConnection,
}: ValidRoleConnectionRowProps) {
  return (
    <div className="flex flex-row items-center gap-2xl">
      <div className="flex items-center gap-sm">
        <div
          className={cn(
            "h-3 w-3 shrink-0 rounded-full",
            isValidRoleArn === undefined
              ? "bg-legacy-neutrals-denseFog"
              : isValidRoleArn === true
                ? "bg-legacy-green-D30"
                : "bg-legacy-red-D20",
          )}
        />
        <span className="text-body-small text-legacy-navy-base">
          Valid Role ARN
        </span>
      </div>
      <div className="flex items-center gap-sm">
        <div
          className={cn(
            "h-3 w-3 shrink-0 rounded-full",
            isValidConnection === undefined
              ? "bg-legacy-neutrals-denseFog"
              : isValidConnection === null
                ? "bg-legacy-green-D30"
                : "bg-legacy-red-D20",
          )}
        />
        <span className="text-body-small text-legacy-navy-base">
          Connection Test
        </span>
      </div>
    </div>
  );
}

function handleErrors(awsAccessRoles: SetAwsAccessRolesMutation$data) {
  // we need errors in ordered format so we can not pass subsequent roles if the previous one has an error
  const errorsList = [
    "primary",
    "readOnly",
    "accountDiscovery",
    "remediationPolicy",
  ];
  const errorsResponse: Record<string, string | null | undefined> = {
    primary: undefined,
    readOnly: undefined,
    accountDiscovery: undefined,
    remediationPolicy: undefined,
  };
  let firstError = false;
  errorsList.forEach((errorKey) => {
    const error =
      awsAccessRoles.setAWSAccessRoles[
        errorKey as keyof typeof awsAccessRoles.setAWSAccessRoles
      ]?.error || (firstError ? undefined : null);
    if (error) firstError = true;
    errorsResponse[errorKey] = error;
  });

  return errorsResponse;
}

export function setAwsAccessRolesUpdater(
  store: RecordSourceSelectorProxy<SetAwsAccessRolesMutation$data>,
  response: SetAwsAccessRolesMutation$data | null | undefined,
) {
  if (!response || !response.setAWSAccessRoles) return;

  const root = store.getRoot();

  const awsAccessRolesRecord = root.getLinkedRecord("awsAccessRoles");
  if (!awsAccessRolesRecord) return;

  Object.keys(response.setAWSAccessRoles).forEach((roleName) => {
    const roles = response.setAWSAccessRoles;
    const arn = roles[roleName as keyof typeof roles]?.arn;
    awsAccessRolesRecord.setValue(arn, `${roleName}RoleARN`);
  });
}

interface Props {
  queryRef: GatewayRolesForm_awsAccessRoles$key;
  viewOnly?: boolean;
}

export function GatewayRolesForm({ queryRef, viewOnly = true }: Props) {
  const { alertDispatch } = useAlertContext();
  const [graphqlError, setGraphqlError] = useState<Error | null>(null);
  const [errors, setErrors] = useState<
    Record<string, string | undefined | null>
  >({});
  const [isViewOnly, setIsViewOnly] = useState(viewOnly);
  const [changesMsg, setChangesMsg] = useState("");

  const { control, handleSubmit, formState, setError, clearErrors, reset } =
    useForm<FormData>({
      mode: "onChange",
      resolver: customZodResolver(schema),
    });

  function customZodResolver(schema: ZodSchema) {
    // Custom resolver to handle the schema level validation
    return async (data: FormData) => {
      const validationResult = schema.safeParse(data);
      const errors: Record<string, { type: string; message: string }> = {};
      if (!validationResult.success) {
        validationResult.error.errors.forEach((error) => {
          const path = error.path.join(".");
          // Add schema-level error
          if (path === "root") {
            setError("root", { type: "manual", message: error.message });
          } else {
            // Add field-level errors
            errors[path] = {
              type: "manual",
              message: error.message,
            };
          }
        });
      } else clearErrors();

      return {
        values: validationResult.success ? validationResult.data : {},
        errors,
      };
    };
  }

  const isValid = formState.isValid;

  function handleFieldChange() {
    if (isViewOnly) return;
    setChangesMsg("Changes not saved");
  }

  const data = useFragment<GatewayRolesForm_awsAccessRoles$key>(
    graphql`
      fragment GatewayRolesForm_awsAccessRoles on Query {
        awsAccessRoles {
          accountDiscoveryRoleARN
          primaryRoleARN
          readOnlyRoleARN
          remediationPolicyRoleARN
          samplePrimaryTrust
        }
      }
    `,
    queryRef,
  );

  const {
    primaryRoleARN,
    readOnlyRoleARN,
    accountDiscoveryRoleARN,
    remediationPolicyRoleARN,
    samplePrimaryTrust,
  } = data.awsAccessRoles;

  const [updateAwsAccessRoles, isInFlight] =
    useMutation<SetAwsAccessRolesMutationType>(SetAwsAccessRolesMutation);

  const handleUpdateAwsAccessRoles = (formData: FormData) => {
    const input = {
      primary: formData.primaryRoleARN,
      readOnly: formData.readOnlyRoleARN,
      accountDiscovery: formData.accountDiscoveryRoleARN,
      remediationPolicy: formData.remediationPolicyRoleARN,
    };

    updateAwsAccessRoles({
      variables: { input },
      onCompleted: (response) => {
        const errorsResponse = handleErrors(response);
        setErrors(errorsResponse);
        setGraphqlError(null);
        const hasError = Object.values(errorsResponse).some(
          (error) => error !== null && error !== undefined,
        );
        if (!hasError) {
          alertDispatch({
            message: "Gateway roles updates successfully",
            severity: "success",
          });
          setChangesMsg("Changes saved");
          setIsViewOnly(true);
          setTimeout(() => {
            setChangesMsg("");
          }, 2000);
        }
      },
      onError: (error) => {
        Sentry.captureException(error);
        setGraphqlError(error);
      },
      updater: setAwsAccessRolesUpdater,
    });
  };
  return (
    <form
      aria-label="upsert-email-form"
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit((data) => {
          handleUpdateAwsAccessRoles(data);
        })(e);
      }}
    >
      <div className="flex flex-col gap-lg">
        <Controller
          control={control}
          defaultValue={primaryRoleARN || ""}
          name="primaryRoleARN"
          render={({ field, fieldState }) => (
            <div className="flex flex-col gap-sm">
              <TextField
                assistiveText={
                  fieldState?.error?.message || errors.primary || undefined
                }
                inputProps={{
                  placeholder: "An AWS Role ARN",
                }}
                isDisabled={isViewOnly}
                isInvalid={fieldState.invalid || Boolean(errors.primary)}
                label="Primary Role"
                {...field}
                onChange={(value) => {
                  field.onChange(value);
                  handleFieldChange();
                }}
              />
              <ValidRoleConnectionRow
                isValidConnection={errors.primary}
                isValidRoleArn={field.value ? !fieldState.invalid : undefined}
              />
            </div>
          )}
        />
        <div className="flex flex-col gap-sm">
          <p className="text-label-medium text-legacy-navy-base">
            Additional Gateway Roles
          </p>
          <p className="text-body-small text-legacy-navy-L30">
            You can also add additional roles to the assume role chain. If the
            primary role is enabled, Stacklet will first assume into the primary
            role and then assume role again to the next role based on the
            context. Optionally, you can supply just the Gateway Roles for each
            context.
          </p>
          <CopyButton
            className="text-body-small"
            copyLabel="Trust Relationship"
            value={samplePrimaryTrust || ""}
          >
            Copy Trust Relationship
          </CopyButton>
        </div>
        <Controller
          control={control}
          defaultValue={readOnlyRoleARN || ""}
          name="readOnlyRoleARN"
          render={({ field, fieldState }) => (
            <div className="flex flex-col gap-sm">
              <TextField
                assistiveText={
                  fieldState?.error?.message || errors.readOnly || undefined
                }
                inputProps={{
                  placeholder: "An AWS Role ARN",
                }}
                isDisabled={isViewOnly}
                isInvalid={fieldState.invalid || Boolean(errors.readOnly)}
                label="Read-Only Role"
                {...field}
                onChange={(value) => {
                  field.onChange(value);
                  handleFieldChange();
                }}
              />
              <ValidRoleConnectionRow
                isValidConnection={errors.readOnly}
                isValidRoleArn={field.value ? !fieldState.invalid : undefined}
              />
            </div>
          )}
        />
        <Controller
          control={control}
          defaultValue={accountDiscoveryRoleARN || ""}
          name="accountDiscoveryRoleARN"
          render={({ field, fieldState }) => (
            <div className="flex flex-col gap-sm">
              <TextField
                assistiveText={
                  fieldState?.error?.message ||
                  errors.accountDiscovery ||
                  undefined
                }
                inputProps={{
                  placeholder: "An AWS Role ARN",
                }}
                isDisabled={isViewOnly}
                isInvalid={
                  fieldState.invalid || Boolean(errors.accountDiscovery)
                }
                label="Account Discover Role"
                {...field}
                onChange={(value) => {
                  field.onChange(value);
                  handleFieldChange();
                }}
              />
              <ValidRoleConnectionRow
                isValidConnection={errors.accountDiscovery}
                isValidRoleArn={field.value ? !fieldState.invalid : undefined}
              />
            </div>
          )}
        />
        <Controller
          control={control}
          defaultValue={remediationPolicyRoleARN || ""}
          name="remediationPolicyRoleARN"
          render={({ field, fieldState }) => (
            <div className="flex flex-col gap-sm">
              <TextField
                assistiveText={
                  fieldState?.error?.message ||
                  errors.remediationPolicy ||
                  undefined
                }
                inputProps={{
                  placeholder: "An AWS Role ARN",
                }}
                isDisabled={isViewOnly}
                isInvalid={
                  fieldState.invalid || Boolean(errors.remediationPolicy)
                }
                label="Policy Remediation Role"
                {...field}
                onChange={(value) => {
                  field.onChange(value);
                  handleFieldChange();
                }}
              />
              <ValidRoleConnectionRow
                isValidConnection={errors.remediationPolicy}
                isValidRoleArn={field.value ? !fieldState.invalid : undefined}
              />
            </div>
          )}
        />

        <div className="flex items-center gap-sm">
          {isViewOnly ? (
            <Button
              className="gap-x-xs"
              onPress={() => setIsViewOnly(false)}
              type="button"
              variant="secondary"
            >
              <EditIcon />
              Edit Configuration
            </Button>
          ) : (
            <ButtonGroup align="start">
              <Button
                isDisabled={isInFlight}
                onPress={() => {
                  setIsViewOnly(true);
                  reset();
                  setChangesMsg("");
                  setErrors({});
                }}
                variant="secondary"
              >
                Cancel
              </Button>

              <Button
                isDisabled={
                  !isValid ||
                  isInFlight ||
                  !formState.isDirty ||
                  Boolean(formState.errors.root)
                }
                isPending={isInFlight}
                pendingText="Saving…"
                type="submit"
                variant="primary"
              >
                Save
              </Button>
            </ButtonGroup>
          )}
          <span className="text-body-small text-legacy-navy-L30">
            {changesMsg}
          </span>
        </div>
        {formState.errors.root &&
        Object.values(formState.touchedFields).some((field) => !!field) ? (
          <p className="text-body-small text-negative-600" role="alert">
            {formState.errors.root.message}
          </p>
        ) : null}
        {graphqlError ? (
          <Alert severity="error">{graphqlError.message}</Alert>
        ) : null}
      </div>
    </form>
  );
}
